import type {JourneyCard, JourneyEnvelope} from '@/Store/journeyStore';

// A card's expires_at is a server-clock epochMs. `offsetMs` (serverClockOffset
// from journeyStore, = server_time - Date.now() at the last applied envelope)
// converts the device's local `nowMs` into the equivalent server time before
// comparing, so a skewed device clock can't keep an expired card alive (or
// hide a live one) client-side.
const isExpired = (
  card: Pick<JourneyCard, 'expires_at'>,
  offsetMs: number,
  nowMs: number,
): boolean => {
  // A malformed expires_at (NaN/Infinity/missing) must never keep a card
  // alive -- `nowMs + offsetMs >= NaN` is always false, which previously let
  // activeCards/dropExpiredCards keep such a card forever while
  // msUntilExpiry (below) fed setTimeout a NaN delay that fires instantly.
  // Treat it as already expired everywhere so the two paths agree.
  if (!Number.isFinite(card.expires_at)) {
    return true;
  }
  return nowMs + offsetMs >= card.expires_at;
};

/**
 * Filters a raw envelope down to the cards that should actually be
 * considered for display: not dismissed, not expired, and none at all while
 * a game is live (a journey nudge must never compete with an in-progress
 * game). Sorted highest-priority first so callers can just take [0] for the
 * Dashboard top slot.
 */
export const activeCards = (
  envelope: {cards?: JourneyEnvelope['cards'] | null} | null | undefined,
  dismissedIds: string[] | null | undefined,
  offsetMs: number,
  nowMs: number,
  isLiveGame: boolean,
): JourneyCard[] => {
  if (isLiveGame) {
    return [];
  }

  const cards = envelope?.cards;
  if (!Array.isArray(cards) || cards.length === 0) {
    return [];
  }

  const dismissed = new Set(dismissedIds || []);
  return cards
    .filter(
      card =>
        card && !dismissed.has(card.id) && !isExpired(card, offsetMs, nowMs),
    )
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
};

/**
 * Strips expired cards out of a card list (used by useJourneySync's
 * visibility_active handler to synchronously purge stale cards from the
 * store the instant the tab becomes visible again -- foreground timers are
 * throttled/frozen while backgrounded, so a card's own auto-hide setTimeout
 * can't be trusted to have fired by itself).
 */
export const dropExpiredCards = (
  cards: JourneyCard[] | null | undefined,
  offsetMs: number,
  nowMs: number,
): JourneyCard[] => {
  if (!Array.isArray(cards)) {
    return [];
  }
  return cards.filter(card => card && !isExpired(card, offsetMs, nowMs));
};

/**
 * Skew-corrected ms remaining until a card expires (floored at 0). A
 * malformed expires_at is treated as already expired (0), matching isExpired
 * above -- a NaN delay handed to setTimeout would otherwise fire instantly
 * anyway, but relying on that coercion left the two paths silently disagreeing.
 */
export const msUntilExpiry = (
  card: Pick<JourneyCard, 'expires_at'>,
  offsetMs: number,
  nowMs: number = Date.now(),
): number => {
  if (!Number.isFinite(card.expires_at)) {
    return 0;
  }
  return Math.max(0, card.expires_at - (nowMs + offsetMs));
};
